import Controller, { OnBeforeSubmit, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { FormAPI, InputAPI, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScoreDiniCreateAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import timeService from '../../../../services/time-service';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../../app/authentication';
import {DomainType, ResponseCreateScoreDINI} from '../../score-types';
import DialogService from '../../../../services/dialog-service';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { WizardSteps } from '../../../../constants/wizards';

@Controller({ component: ScoreDiniCreateAliases.SCORE_DINI_ADD_LAYOUT, fragment: FragmentNames.SCORE_DINI_CREATE })
export default class ScoreDiniCreateController extends BaseController {

  @Inject(ScoreDiniCreateAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreDiniCreateAliases.FORM_ONE)
  private formOneScoreDiniCreateAPI!: FormAPI;

  @Inject(ScoreDiniCreateAliases.FORM_TWO)
  private formTwoScoreDiniCreateAPI!: FormAPI;

  @Inject(ScoreDiniCreateAliases.WIZARD)
  private wizard!: WizardAPI;

  @OnValidate(ScoreDiniCreateAliases.FORM_ONE)
  handleOnValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_ONE, validation.valid);
  }

  @OnValidate(ScoreDiniCreateAliases.FORM_TWO)
  handleOnSecondValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_TWO, validation.valid);
  }

  @OnMount({ target: ScoreDiniCreateAliases.WIZARD })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_DINI
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
    timeService.getCurrentDateTime()
      .then(date => this.setupDateInputApi.setValue(date))
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-dini-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private releaseDINIScore(sequenceNumber: number): void {
    this.formOneScoreDiniCreateAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_SCORE_DINI, { sequenceNumber })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneScoreDiniCreateAPI.setLoading(false)
      });
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseDINIScore(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private createNewDINIScore(body: Record<string, Value>): void {
    this.formTwoScoreDiniCreateAPI.setLoading(true);
    HttpService.post(Endpoint.POST_CREATE_SCORE_DINI, undefined, {body})
      .then(response => {
        const {idScoreIndexes} = response.body as ResponseCreateScoreDINI;
        this.showModalSubmit(idScoreIndexes);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formTwoScoreDiniCreateAPI.setLoading(false));
  }

  @OnBeforeSubmit({ target: ScoreDiniCreateAliases.WIZARD })
  handleBeforeSubmit(): void {
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    const isCreated = this.context.get('isCreated');
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formDataOne = this.formOneScoreDiniCreateAPI.getRecord();
    const formDataTwo = this.formTwoScoreDiniCreateAPI.getRecord();
    const mucosalCutaneousIntegrity = formDataOne.mucosalCutaneousIntegrity as DomainType;
    const networkSupport = formDataTwo.networkSupport as DomainType;
    const oxygenation = formDataOne.oxygenation as DomainType;
    const intervControles = formDataOne.intervControles as DomainType;
    const partCompanion = formDataTwo.partCompanion as DomainType;
    const activity = formDataOne.activity as DomainType;
    const bodyHygiene = formDataTwo.bodyHygiene as DomainType;
    const drugTherapy = formDataOne.drugTherapy as DomainType;
    const elimination = formDataTwo.eliminations as DomainType;
    const foodAndHydration = formDataTwo.foodAndHydration as DomainType;
    const mobility = formDataTwo.mobility as DomainType;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const evaluationDate = moment(formDataOne.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);

    const body = {
      activity: activity.valueDomain,
      attendance: encounterId as number,
      bodyHygiene: bodyHygiene.valueDomain,
      drugTherapy: drugTherapy.valueDomain,
      eliminations: elimination.valueDomain,
      evaluationDate,
      foodAndHydration: foodAndHydration.valueDomain,
      idScoreIndexes: null,
      intervControles: intervControles.valueDomain,
      mobility: mobility.valueDomain,
      mucosalCutaneousIntegrity: mucosalCutaneousIntegrity.valueDomain,
      networkSupport: networkSupport.valueDomain,
      oxygenation: oxygenation.valueDomain,
      partCompanion: partCompanion.valueDomain,
      professionalCode: user.physicalPersonId,
    } as Record<string, Value>;
    this.createNewDINIScore(body);
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  @OnUnmount(ScoreDiniCreateAliases.SCORE_DINI_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_DINI;
  }
}
